/**
 * One team as a Desk tile on the admin console (#2833): a header band with
 * the team name and its cast character, then Owner, Lead, Members and
 * Roster. The Home `TeamTile` stays as it is, because it carries Home data.
 *
 * `footer` is a named slot. The later slices (adoption, setup progress,
 * recent changes) fill it. With nothing passed, the tile shows no footer.
 */
import type { ReactNode } from "react";

import type { AdminAllTeamsRow } from "../../../api/useAdminTeams";
import { cn } from "../../../lib/cn";
import { castFor } from "../../home/homeOverview";
import { LabelMark, PriorityMark } from "./TeamMarks";
import { ownerLabel, personLabel, spanWords, splitTeamName } from "./adminWords";
import { memberWords } from "./memberWords";
import type { TeamFlag } from "./teamFlags";
import { teamKicker } from "./teamKind";

export interface AdminTeamTileProps {
  row: AdminAllTeamsRow;
  selected?: boolean;
  /** Mix-ups the checks found for this team. */
  flags?: readonly TeamFlag[];
  onOpen: (teamId: string) => void;
  /** The line under the header (#2872): the attention reason in amber, or the active count. */
  leadLine?: { text: string; tone: "warning" | "plain" } | null | undefined;
  /** Slot for the adoption, setup and recent-changes blocks of later slices. */
  footer?: ReactNode;
}

/** A small amber pill naming one mix-up. */
export function MixupFlag({
  children,
  className,
  tone = "warning",
}: {
  children: ReactNode;
  className?: string;
  /** Amber for a mix-up, grey for a plain state, red for a deleted account. */
  tone?: "warning" | "neutral" | "danger";
}) {
  return (
    <span
      className={cn(
        "ml-1.5 inline-flex shrink-0 whitespace-nowrap rounded-full px-2 py-px text-xs font-semibold",
        tone === "warning" && "bg-warning-soft text-warning",
        tone === "neutral" && "bg-bg-subtle text-fg-muted",
        tone === "danger" && "bg-danger-soft text-danger",
        className,
      )}
    >
      {children}
    </span>
  );
}

function Line({
  label,
  testid,
  children,
}: {
  label: string;
  testid?: string;
  children: ReactNode;
}) {
  return (
    <div className="flex items-baseline gap-3" data-testid={testid}>
      <dt className="w-14 shrink-0 text-xs text-fg-faint">{label}</dt>
      <dd className="flex min-w-0 items-baseline text-fg-muted">{children}</dd>
    </div>
  );
}

export function AdminTeamTile({
  row,
  selected = false,
  flags = [],
  onOpen,
  leadLine,
  footer,
}: AdminTeamTileProps) {
  const kicker = teamKicker(row);
  const leads = row.leads ?? [];
  const noLead = flags.includes("No lead") || leads.length === 0;
  const shownFlags: TeamFlag[] = [
    ...(["No seat", "Same name"] as const).filter((f) => flags.includes(f)),
    ...(noLead ? (["No lead"] as const) : []),
  ];
  const roster = spanWords(row.latest_cycle_start, row.latest_cycle_end);
  const { org, team } = splitTeamName(row.name);

  return (
    <article
      className="group relative flex flex-col transition-colors"
      data-testid={`admin-team-tile-${row.team_id}`}
    >
      <div
        className={cn(
          "flex min-h-[104px] items-end justify-between gap-3 border-b pt-2 transition-colors",
          selected
            ? "border-accent"
            : "border-border group-focus-within:border-accent group-hover:border-accent/40",
        )}
      >
        <div className="min-w-0 flex-1 self-start pb-2">
          {kicker ? <p className="text-xs text-fg-muted">{kicker}</p> : null}
          {org ? (
            <p className="text-xs text-fg-muted" data-testid={`admin-tile-org-${row.team_id}`}>
              {org}
            </p>
          ) : null}
          <h3 className="text-[18px] font-semibold leading-tight text-fg">
            <button
              type="button"
              onClick={() => onOpen(row.team_id)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  onOpen(row.team_id);
                }
              }}
              className="text-left after:absolute after:inset-0 after:z-0 focus-visible:outline-none focus-visible:after:ring-2 focus-visible:after:ring-inset focus-visible:after:ring-accent"
              data-testid={`admin-team-tile-open-${row.team_id}`}
            >
              {team}
            </button>
          </h3>
          <p
            className="mt-1.5 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs text-fg-muted"
            data-testid={`admin-tile-marks-${row.team_id}`}
          >
            <PriorityMark row={row} />
            {/* Each dot sits in the unit of the item after it: a wrap moves the dot too. */}
            {row.admin_label?.trim() ? (
              <span
                className="max-w-full truncate whitespace-nowrap"
                title={row.admin_label?.trim()}
              >
                <span aria-hidden="true">{"\u00b7"}</span> <LabelMark row={row} />
              </span>
            ) : null}
            {shownFlags.map((f) => (
              <span key={f} className="whitespace-nowrap">
                <span aria-hidden="true">{"\u00b7"}</span>{" "}
                <span className="font-medium text-warning" data-testid="admin-tile-flag">
                  {f}
                </span>
              </span>
            ))}
          </p>
        </div>
        <img
          src={`/site/characters/${castFor(row.team_id)}/idle.svg`}
          alt=""
          aria-hidden="true"
          className="pointer-events-none mr-6 h-[104px] shrink-0 select-none"
          data-testid="admin-tile-character"
        />
      </div>
      {leadLine ? (
        <p
          className={cn(
            "pt-3 text-sm font-medium",
            leadLine.tone === "warning" ? "text-warning" : "text-fg-muted",
          )}
          data-testid={`admin-tile-lead-line-${row.team_id}`}
        >
          {leadLine.text}
        </p>
      ) : null}
      <dl className="flex flex-col gap-1.5 py-4 text-sm">
        <Line label="Owner" testid={`admin-tile-owner-${row.team_id}`}>
          {row.owner_email ? (
            // biome-ignore lint/a11y/useKeyWithClickEvents: the team name button is the keyboard target
            <b
              className="relative z-10 break-words font-semibold text-fg"
              title={row.owner_email}
              onClick={() => onOpen(row.team_id)}
            >
              {ownerLabel(row)}
            </b>
          ) : (
            "None"
          )}
        </Line>
        <Line label="Lead" testid={`admin-tile-lead-${row.team_id}`}>
          {leads.length > 0 ? (
            // biome-ignore lint/a11y/useKeyWithClickEvents: the team name button is the keyboard target
            <b
              className="relative z-10 break-words font-semibold text-fg"
              onClick={() => onOpen(row.team_id)}
              title={(leads[0] as (typeof leads)[number]).email}
            >
              {personLabel(leads[0] as (typeof leads)[number])}
              {leads.length > 1 ? (
                <span className="ml-1 font-normal text-fg-faint">+{leads.length - 1}</span>
              ) : null}
            </b>
          ) : null}
        </Line>
        <Line label="Members">
          <b className="font-semibold text-fg">{memberWords(row.member_counts)}</b>
        </Line>
        <Line label="Roster">{roster ?? "None yet"}</Line>
      </dl>
      {footer ? (
        <div
          className="relative z-10 mb-4 border-t border-dashed border-border pt-3 text-xs text-fg-muted"
          data-testid={`admin-team-tile-footer-${row.team_id}`}
        >
          {footer}
        </div>
      ) : null}
    </article>
  );
}
